import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CoreValues = () => {
  const [expandedValue, setExpandedValue] = useState(null);

  const coreValues = [
    {
      id: 'innovation',
      title: 'Innovation Excellence',
      icon: 'Lightbulb',
      color: 'primary',
      shortDescription: 'Pushing the boundaries of what\'s possible with AI technology',
      fullDescription: 'We believe in continuous innovation and staying at the forefront of AI technology. Our team constantly explores new methodologies, tools, and approaches to ensure our clients benefit from the latest advancements in artificial intelligence. We foster a culture of experimentation and learning, where breakthrough ideas are encouraged and nurtured.',
      principles: [
        'Continuous learning and adaptation',
        'Experimental mindset with calculated risks',
        'Investment in cutting-edge research and development',
        'Collaboration with leading academic institutions'
      ]
    },
    {
      id: 'integrity',
      title: 'Ethical Integrity',
      icon: 'Shield',
      color: 'success',
      shortDescription: 'Maintaining the highest standards of ethics and transparency',
      fullDescription: 'Ethical AI is not just a buzzword for us—it\'s fundamental to everything we do. We ensure that all our AI solutions are developed and deployed with careful consideration of their impact on society, privacy, and fairness. Our commitment to transparency means clients always understand how our solutions work and make decisions.',
      principles: [
        'Transparent AI decision-making processes',
        'Privacy-first data handling practices',
        'Bias detection and mitigation strategies',
        'Responsible AI governance frameworks'
      ]
    },
    {
      id: 'collaboration',
      title: 'Partnership Collaboration',
      icon: 'Users',
      color: 'accent',
      shortDescription: 'Building lasting partnerships through deep collaboration',
      fullDescription: 'We view our clients as true partners in transformation. Our collaborative approach means we work closely with your team, sharing knowledge and building capabilities together. This partnership model ensures sustainable success and empowers your organization to continue innovating long after project completion.',
      principles: [
        'Co-creation with client teams',
        'Knowledge transfer and capability building',
        'Long-term relationship focus',
        'Cross-functional collaboration expertise'
      ]
    },
    {
      id: 'excellence',
      title: 'Delivery Excellence',
      icon: 'Target',
      color: 'warning',
      shortDescription: 'Delivering exceptional results that exceed expectations',
      fullDescription: 'Excellence is our standard, not our goal. We maintain rigorous quality assurance processes, continuous monitoring, and iterative improvement methodologies. Our track record of 98% client satisfaction reflects our unwavering commitment to delivering solutions that not only meet requirements but drive transformational business outcomes.',
      principles: [
        'Rigorous quality assurance processes',
        'Continuous performance monitoring',
        'Client success-driven metrics',
        'Iterative improvement methodologies'
      ]
    }
  ];

  const toggleExpanded = (valueId) => {
    setExpandedValue(expandedValue === valueId ? null : valueId);
  };

  const getColorClass = (color) => {
    const colorMap = {
      primary: 'bg-primary text-white',
      success: 'bg-success text-white',
      accent: 'bg-accent text-white',
      warning: 'bg-warning text-white'
    };
    return colorMap[color] || 'bg-primary text-white';
  };

  return (
    <section>
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-4">Our Core Values</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          The principles that guide every decision we make and every solution we deliver
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {coreValues?.map((value) => (
          <div 
            key={value?.id}
            className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden hover:shadow-elevation transition-smooth"
          >
            <div className="p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClass(value?.color)}`}>
                  <Icon name={value?.icon} size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-foreground mb-2">{value?.title}</h3>
                  <p className="text-muted-foreground mb-4">{value?.shortDescription}</p>
                  
                  <button
                    onClick={() => toggleExpanded(value?.id)}
                    className="flex items-center space-x-2 text-primary hover:text-primary/80 font-medium transition-smooth"
                  >
                    <span>{expandedValue === value?.id ? 'Show Less' : 'Learn More'}</span>
                    <Icon 
                      name={expandedValue === value?.id ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              <div className={`overflow-hidden transition-all duration-300 ${
                expandedValue === value?.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="pt-4 border-t border-border">
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {value?.fullDescription}
                  </p>
                  
                  <div>
                    <h4 className="font-semibold text-foreground mb-3">Key Principles:</h4>
                    <div className="space-y-2">
                      {value?.principles?.map((principle, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className={`w-2 h-2 rounded-full ${
                            value?.color === 'primary' ? 'bg-primary' :
                            value?.color === 'success' ? 'bg-success' :
                            value?.color === 'accent'? 'bg-accent' : 'bg-warning'
                          }`}></div>
                          <span className="text-sm text-muted-foreground">{principle}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Leadership Philosophy */}
      <div className="mt-16 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 lg:p-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full gradient-primary flex items-center justify-center">
            <Icon name="Quote" size={32} color="white" />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-4">Leadership Philosophy</h3>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <blockquote className="text-xl text-center text-muted-foreground italic mb-6 leading-relaxed">
            "True leadership in AI consulting isn't about having all the answers—it's about asking the right questions, 
            fostering collaborative discovery, and empowering our clients to build sustainable AI capabilities that drive 
            lasting transformation."
          </blockquote>
          
          <div className="text-center">
            <div className="font-semibold text-foreground">Dr. Sarah Chen</div>
            <div className="text-sm text-muted-foreground">Chief Executive Officer & Founder</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoreValues;